/**
 * WT-3b (fieldiq-api#1252) — the data-logger import's words and display rules.
 * Pure functions, so each can be pinned without a screen.
 *
 * NOTHING HERE COMPUTES OR CONVERTS A VALUE. A proposed row carries each value
 * exactly as the logger file wrote it (`row.<key>`) with the unit the API
 * resolved (`row.<key>_unit`); the screen shows those two strings side by side.
 */
import { formatInstant } from './calendar-date';
import { wtRefusal, wtUnitLabel, type WtSkin } from './well-testing';
import type { WtImportColumn, WtImportResult, WtImportRow, WtImportRowStatus } from '../types/well-testing';

/** Only an `ok` row is a row the confirm will save. */
export const wtImportRowKeepable = (status: string): boolean => status === 'ok';

/**
 * One hue per meaning: ready green, refused red, both "will not be saved
 * because it is already there" kinds slate. Exhaustive over the API's statuses;
 * anything else is the unknown skin, never a real status.
 */
export const WT_IMPORT_STATUS_SKIN: Record<WtImportRowStatus, WtSkin> = {
  ok: { label: 'Ready', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  invalid: { label: 'Refused', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  duplicate: { label: 'Already on job', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  duplicate_in_batch: { label: 'Repeated', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
const UNKNOWN_SKIN: WtSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export const wtImportStatusSkin = (status: string): WtSkin =>
  (WT_IMPORT_STATUS_SKIN as Record<string, WtSkin>)[status] ?? UNKNOWN_SKIN;

/** Where a column's unit came from, in words. */
export function wtImportUnitSource(c: Pick<WtImportColumn, 'unitSource' | 'unitStated'>): string {
  switch (c.unitSource) {
    case 'header':
      return c.unitStated ? `from header ("${c.unitStated}")` : 'from header';
    case 'tenant_default':
      return 'tenant default — the file states none';
    case 'fixed':
      return "the field's own unit";
    default:
      return '—';
  }
}

/** A value as the file wrote it, with its unit. Absent → `—`, never 0. */
export function wtImportValue(row: WtImportRow | null | undefined, key: string): string {
  const v = row?.[key];
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return '—';
  const unit = row?.[`${key}_unit`];
  return typeof unit === 'string' && unit ? `${String(v)} ${wtUnitLabel(unit)}` : String(v);
}

const WALL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/;

/**
 * A proposed row's read_at in the tenant's zone.
 *
 * The API sends a zone-less wall clock (`2026-10-29T06:15:00`) when the file
 * stated no zone — it IS the tenant's clock, so it is shown as written, never
 * re-read in the device's zone. A stamp with its own offset is an instant,
 * shown in the tenant's zone. Anything else is the file's text, verbatim (the
 * row's reason says why it will not be saved).
 */
export function wtImportReadAt(text: unknown, timeZone: string | null | undefined): string {
  if (typeof text !== 'string' || text.trim() === '') return '—';
  // A wall clock read AS UTC and shown in UTC is the same clock face, with no device zone involved.
  if (WALL.test(text)) return formatInstant(`${text.length === 16 ? `${text}:00` : text}Z`, 'UTC') ?? text;
  if (ZONED.test(text)) return formatInstant(text, timeZone) ?? text;
  return text;
}

/** The reading columns the rows carry, in the file's column order, one per field. */
export function wtImportValueColumns(map: WtImportColumn[]): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  map.forEach((c) => {
    if (!c.field || c.field === 'notes' || out.some((o) => o.key === c.field)) return;
    out.push({ key: c.field, label: c.label ?? c.field });
  });
  return out;
}

/**
 * The result in one sentence. When every row was skipped because the job
 * already had it, that is said unmistakably, so nobody presses Confirm again.
 */
export function wtImportResultSentence(counts: WtImportResult['counts']): string {
  const { inserted, skipped } = counts;
  if (inserted === 0 && skipped > 0) {
    return skipped === 1
      ? 'Nothing imported — the 1 row was already on this job.'
      : `Nothing imported — all ${skipped} rows were already on this job.`;
  }
  const saved = `Imported ${inserted} reading${inserted === 1 ? '' : 's'}.`;
  return skipped > 0 ? `${saved} ${skipped} row${skipped === 1 ? ' was' : 's were'} skipped — see below.` : saved;
}

export const WT_IMPORT_SKIP_LABEL: Record<string, string> = {
  duplicate: 'Already on this job',
  duplicate_in_batch: 'Same time as an earlier row',
};

/** Every refusal the three import endpoints send, in plain words. */
export const WT_IMPORT_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  ai_unavailable: () => 'AI reading of logger files is not available right now. Nothing was saved. Enter the readings by hand, or try again later.',
  ai_parse_failed: (x) =>
    typeof x.error === 'string' && x.error ? x.error : 'The AI could not read the file. Nothing was saved.',
  job_not_scheduled: () => 'This job is still a draft. Schedule it before importing readings.',
  job_closed: () => 'This job is completed or cancelled, so it takes no more readings.',
  run_dismissed: () => 'That parse was dismissed. Choose the file and parse it again to import it.',
  run_not_pending: (x) =>
    `That parse is already ${typeof x.outcome === 'string' ? x.outcome.replace(/_/g, ' ') : 'closed'}, so it has nothing left to act on. Parse the file again.`,
};

/**
 * An import refusal as one sentence. A confirm's 400 names `rows[i].field`
 * where i indexes the rows SENT; `refs` maps that back to the file's line.
 */
export function wtImportRefusal(err: unknown, fallback: string, refs: (string | null)[] = []): string {
  const msg = wtRefusal(err, fallback, WT_IMPORT_REFUSALS);
  return msg.replace(/rows\[(\d+)\]/g, (_, i) => `row ${refs[Number(i)] ?? Number(i) + 1}`);
}
